import { useState } from "react"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconGlobe2Outline18 as Globe,
	IconArrowsRotateCenterOutline18 as RefreshCcw,
	IconTrash2Outline18 as Trash2,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import type { Workspace } from "@productlane/zero"
import {
	AlertDialog,
	Badge,
	Button,
	CopyButton,
	DropdownMenu,
	DropdownMenuItem,
	Form,
	FormInput,
	IconButton,
	Loader,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"
import { EllipsisIcon } from "@productlane/ui/src/icons/project/ellipsis-icon"

import { DnsEntryTable } from "@/components/dns-entry-table"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useZeroWorkspace } from "@/lib/zero"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsHeader } from "../settings-header"
import {
	dnsHostLabel,
	DomainProgress,
	domainRefreshToast,
	previewDomainSummary,
} from "./domain-progress"

const DomainFormSchema = z.object({
	domain: z
		.string()
		.min(1)
		.regex(
			/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/,
			"Please enter a valid domain name (e.g., test.com, feedback.yourdomain.com)",
		),
})

const STATUS_POLL_INTERVAL = 15000

export function DomainSettingsPage() {
	const workspace = useZeroWorkspace()

	return (
		<>
			<MetaInfo title="Custom domain" />

			<SettingsHeader
				title="Custom domain"
				subtitle="Bring your own domain to Productlane."
				docsArticle="fb795077-8e31-4316-a411-00696370f15c"
				action={workspace?.customDomain ? <DomainHeaderActions /> : null}
			/>
			{workspace ? (
				<DomainForm
					key={workspace.customDomain ?? "none"}
					data={{ workspace: workspace }}
				/>
			) : (
				<div className="flex items-center justify-center">
					<Loader />
				</div>
			)}
		</>
	)
}

function DomainHeaderActions() {
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
	const workspace = useZeroWorkspace()
	const ctx = api.useUtils()
	const domainQuery = api.workspace.getCustomDomainInfo.useQuery(undefined, {
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		staleTime: Infinity,
	})
	const resetCustomDomainMutation = api.workspace.resetCustomDomain.useMutation(
		{
			onSuccess: async () => {
				await ctx.workspace.info.invalidate()
				await ctx.workspace.getCustomDomainInfo.reset()
				toast.success({
					title: "Custom domain successfully reset",
					icon: Globe,
				})
			},
			onError: (error) => {
				toast.error({
					title: "Couldn't reset custom domain",
					description: error.message,
					icon: Globe,
				})
			},
		},
	)

	return (
		<>
			<AlertDialog
				isOpen={isDeleteConfirmOpen}
				onOpenChange={setIsDeleteConfirmOpen}
				confirmText="Delete domain"
				title="Delete custom domain?"
				description={`This will remove your custom domain "${workspace?.customDomain ?? ""}" and you'll be able to set a new one. This action cannot be undone.`}
				onCancel={() => {
					setIsDeleteConfirmOpen(false)
					analytics.track("cancel-reset-domain-dialog")
				}}
				onConfirm={() => {
					resetCustomDomainMutation.mutate({})
					setIsDeleteConfirmOpen(false)
					analytics.track("reset-custom-domain")
				}}
			/>

			<DropdownMenu
				trigger={<IconButton variant="outline" Icon={EllipsisIcon} />}
				contentProps={{ align: "end" }}
			>
				<DropdownMenuItem
					Icon={RefreshCcw}
					disabled={isRefreshing}
					onSelect={(event) => {
						event.preventDefault()
						analytics.track("refetch-custom-domain-status")
						setIsRefreshing(true)
						void domainQuery
							.refetch()
							.then((result) => {
								const refreshed = result.data?.summary
								if (!refreshed) return
								const { kind, title, description } =
									domainRefreshToast(refreshed)
								toast[kind]({ title, description, icon: Globe })
							})
							.finally(() => setIsRefreshing(false))
					}}
				>
					Refresh domain status
				</DropdownMenuItem>
				<DropdownMenuItem
					Icon={Trash2}
					className="text-red-600 dark:text-red-500"
					iconClassName="text-red-600 dark:text-red-500"
					isLoading={resetCustomDomainMutation.isLoading}
					onSelect={() => {
						analytics.track("open-reset-domain-dialog")
						setIsDeleteConfirmOpen(true)
					}}
				>
					Delete domain
				</DropdownMenuItem>
			</DropdownMenu>
		</>
	)
}

interface DomainFormProps {
	data: {
		workspace: Workspace
	}
}

function DomainForm({ data }: DomainFormProps) {
	const domainQuery = api.workspace.getCustomDomainInfo.useQuery(undefined, {
		enabled: !!data.workspace.customDomain,
		refetchInterval: (queryData) =>
			queryData?.summary.state === "active" ? false : STATUS_POLL_INTERVAL,
		refetchOnWindowFocus: true,
	})
	const domainConnectQuery = api.workspace.getDomainConnectSetup.useQuery(
		undefined,
		{
			enabled: !!data.workspace.customDomain,
			staleTime: 5 * 60 * 1000,
		},
	)
	const form = useZodForm({
		schema: DomainFormSchema,
		defaultValues: {
			domain: data.workspace.customDomain ?? "",
		},
	})
	const ctx = api.useUtils()
	const setCustomDomainMutation = api.workspace.setCustomDomain.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
			toast.success({
				title: "Custom domain set, now add the CNAME record",
				icon: Globe,
			})
		},
		onError: (error) => {
			toast.error({
				title: "Couldn't update custom domain",
				description: error.message,
				icon: Globe,
			})
		},
	})

	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const [showDnsSettings, setShowDnsSettings] = useState(false)
	const domainWatch = form.watch("domain")

	if (domainQuery.isLoading && !!data.workspace.customDomain) {
		return (
			<div className="flex items-center justify-center">
				<Loader />
			</div>
		)
	}

	if (domainQuery.data) {
		const summary = previewDomainSummary(
			domainQuery.data.summary,
			window.location.search,
			import.meta.env.DEV,
		)
		const isActive = summary.state === "active"
		const cnameValue = domainQuery.data.expectedCname
		const dns = domainQuery.data.dns

		const txtRecords = (() => {
			const records = domainQuery.data.ssl.validation_records
				?.filter((r) => !!r.txt_name && !!r.txt_value)
				.map((r) => ({
					txt_name: r.txt_name!,
					txt_value: r.txt_value!,
				}))
			if (records && records.length > 0) return records
			if (domainQuery.data.ssl.txt_name && domainQuery.data.ssl.txt_value) {
				return [
					{
						txt_name: domainQuery.data.ssl.txt_name,
						txt_value: domainQuery.data.ssl.txt_value,
					},
				]
			}
			return []
		})()
		const txtDataAvailable = txtRecords.length > 0

		if (isActive) {
			return (
				<>
					<DomainProgress
						className="mb-4"
						hostname={domainQuery.data.hostname}
						summary={summary}
						dnsFound={dns ? dns.pointsToProductlane : null}
						createdAt={domainQuery.data.created_at}
					/>

					<div className="mb-4 flex justify-start">
						<Button
							variant="outline"
							iconRight={ChevronRight}
							iconRightClassName={showDnsSettings ? "rotate-90" : ""}
							onClick={() => setShowDnsSettings((prev) => !prev)}
						>
							{showDnsSettings ? "Hide DNS records" : "Show DNS records"}
						</Button>
					</div>

					{showDnsSettings ? (
						<>
							<SettingsContainer className="space-y-3 divide-y-0">
								<DnsRecordCard
									type="CNAME"
									host={domainQuery.data.hostname}
									value={cnameValue}
								/>
								{txtDataAvailable ? (
									<DnsRecordCard
										type="TXT"
										host={txtRecords[0]!.txt_name}
										value={txtRecords[0]!.txt_value}
									/>
								) : null}
							</SettingsContainer>
							<p className="mb-8 mt-3 text-sm text-secondary">
								Only the CNAME record is required to keep your domain connected.
								{txtDataAvailable
									? " The TXT record was used once to verify ownership and issue the SSL certificate; it is no longer needed and can be removed."
									: ""}
							</p>
						</>
					) : null}
				</>
			)
		}

		return (
			<>
				<DomainProgress
					className="mb-8"
					hostname={domainQuery.data.hostname}
					summary={summary}
					dnsFound={dns ? dns.pointsToProductlane : null}
					createdAt={domainQuery.data.created_at}
				/>

				{domainConnectQuery.data ? (
					<DomainConnectCard
						providerName={domainConnectQuery.data.providerName}
						applyUrl={domainConnectQuery.data.applyUrl}
					/>
				) : null}

				<div className="mb-10">
					<p className="mb-1 font-medium">
						{domainConnectQuery.data
							? "Or add the CNAME record yourself"
							: "Add this CNAME record"}
					</p>
					<SettingsContainer className="mt-3 space-y-0 divide-y-0 max-sm:[&>*]:!py-0">
						<DnsEntryTable
							entries={[
								{
									type: "CNAME",
									host: dnsHostLabel(domainQuery.data.hostname),
									value: cnameValue,
									proxy: "DNS only",
								},
							]}
						/>
					</SettingsContainer>
				</div>

				{txtDataAvailable ? (
					<div className="mb-8">
						<div className="mb-1 flex items-center gap-2">
							<p className="font-medium">
								Add this <b>TXT</b> record to verify your domain ownership:
							</p>
							{dns ? (
								<Badge variant={dns.acmeTxtFound ? "green" : "important"}>
									{dns.acmeTxtFound ? "Found" : "Not found yet"}
								</Badge>
							) : null}
						</div>
						<p className="mb-4 text-sm text-gray-500">
							This record proves you own the domain so Cloudflare can issue your
							SSL certificate.
						</p>
						<SettingsContainer>
							<DnsEntryTable
								entries={txtRecords.map((r) => ({
									type: "TXT" as const,
									host: r.txt_name,
									value: r.txt_value,
								}))}
							/>
						</SettingsContainer>
					</div>
				) : null}

				<p className="text-sm text-secondary">
					Need help?{" "}
					<Button
						variant="link"
						className="inline"
						onClick={() => {
							// @ts-expect-error Global widget calls
							// eslint-disable-next-line
							window.Productlane.open("feedback")
						}}
					>
						Contact us
					</Button>
					.
				</p>
			</>
		)
	}

	return (
		<SettingsContainer>
			<AlertDialog
				isOpen={isConfirmOpen}
				onOpenChange={setIsConfirmOpen}
				variant="positive"
				confirmText="Confirm"
				title={`Set custom domain to ${domainWatch}?`}
				description="Please make sure you've entered the domain correctly."
				onCancel={() => {
					setIsConfirmOpen(false)
					analytics.track("cancel-confirm-domain-dialog")
				}}
				onConfirm={() => {
					setCustomDomainMutation.mutate({ domain: form.getValues().domain })
					setIsConfirmOpen(false)
					analytics.track("set-custom-domain")
				}}
			/>
			<Form
				className="space-y-4"
				form={form}
				onSubmit={() => {
					analytics.track("open-confirm-domain-dialog")
					setIsConfirmOpen(true)
				}}
			>
				<FormInput
					name="domain"
					label="Your domain"
					placeholder="feedback.yourdomain.com"
					readOnly={false}
				/>
				<div className="text-sm text-secondary">
					Set a custom domain for your public pages. Find the documentation{" "}
					<Button
						variant="link"
						className="inline"
						onClick={() => {
							// @ts-expect-error Global widget calls
							// eslint-disable-next-line
							window.Productlane.openDocs(
								"fb795077-8e31-4316-a411-00696370f15c",
							)
						}}
					>
						here
					</Button>
					.
				</div>
				<div className="flex justify-end">
					<SubmitButton isLoading={setCustomDomainMutation.isLoading}>
						Update
					</SubmitButton>
				</div>
			</Form>
		</SettingsContainer>
	)
}

function DomainConnectCard({
	providerName,
	applyUrl,
}: {
	providerName: string
	applyUrl: string
}) {
	return (
		<SettingsContainer className="mb-8">
			<SettingItem
				title={`Add the record in ${providerName}`}
				description={`${providerName} hosts this domain and can write the CNAME for you.`}
				action={
					<Button
						variant="outline"
						onClick={() => {
							analytics.track("open-domain-connect-flow")
							window.open(applyUrl, "_blank", "noopener,noreferrer")
						}}
					>
						Set up in {providerName}
					</Button>
				}
			/>
		</SettingsContainer>
	)
}

function DnsRecordCard({
	type,
	host,
	value,
}: {
	type: string
	host: string
	value: string
}) {
	return (
		<div className="space-y-2 rounded-lg bg-gray-100 p-3 pr-6 dark:bg-gray-800">
			<div className="flex items-center">
				<div className="w-1/3 text-sm text-secondary">Type</div>
				<div className="flex w-2/3 items-center gap-2">
					<div className="font-mono">{type}</div>
					<Badge variant="green">Active</Badge>
				</div>
			</div>
			<div className="flex items-center">
				<div className="w-1/3 shrink-0 text-sm text-secondary">Host</div>
				<div className="flex w-2/3 min-w-0 items-center gap-2">
					<div className="truncate font-mono text-sm">{host}</div>
					<CopyButton
						value={host}
						className="!text-tertiary hover:!text-primary"
					/>
				</div>
			</div>
			<div className="flex items-center">
				<div className="w-1/3 shrink-0 text-sm text-secondary">Value</div>
				<div className="flex w-2/3 min-w-0 items-center gap-2">
					<div className="truncate font-mono text-sm">{value}</div>
					<CopyButton
						value={value}
						className="!text-tertiary hover:!text-primary"
					/>
				</div>
			</div>
		</div>
	)
}
